import ResponsiveStage from "@/components/konva/stage/ResponsiveStage.tsx";
import PlayerMapLayers from "@/tabletop/PlayerMapLayers.tsx";
import DungeonMapLayer from "@/tabletop/dungeon/DungeonMapLayer.tsx";
import DungeonGridLayer from "@/tabletop/dungeon/DungeonGridLayer.tsx";
import InitiativeBar from "@/tabletop/tokens/InitiativeBar.tsx";
import {useAreaEffect} from "@/encounter/effect/AreaEffectHooks.ts";
import AreaEffectAppearance from "@/tabletop/effects/AreaEffectAppearance.tsx";
import TokenLayer from "@/tabletop/tokens/TokenLayer.tsx";
import {useEncounter} from "@/encounter/EncounterHooks.ts";
import {useVisibility} from "@/tabletop/visibility/useVisibility.ts";
import {useStage} from "@/components/konva/stage/ResponsiveStageHooks.ts";
import {usePlayerViewPort} from "@/encounter/viewport/PlayerViewPortHooks.ts";
import {coverViewPort} from "@/encounter/model/ViewPort.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {effectIntersectsBounds, worldBoundsForViewport} from "@/tabletop/visibility/ViewportCulling.ts";
import type {Visibility} from "@/tabletop/visibility/VisibilityModel.ts";
import {useFogMask} from "@/tabletop/visibility/useFogMask.ts";
import {useInterpolatedPlayerViewport} from "./useInterpolatedPlayerViewport.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {effectsOnLevel} from "@/tabletop/effects/AreaEffectLevel.ts";
import CombatPreparationOverlay from "./CombatPreparationOverlay.tsx";

export default function PlayerTableView() {
    const {effects} = useAreaEffect();
    const visibility = useVisibility("friendly");

    return <div className="relative size-full">
        <ResponsiveStage>
            <PlayerMapContent visibility={visibility} effects={effects}/>
        </ResponsiveStage>
        <InitiativeBar playerView/>
        <CombatPreparationOverlay/>
    </div>;
}

function PlayerMapContent({visibility, effects}: {
    visibility: Visibility;
    effects: ReturnType<typeof useAreaEffect>["effects"];
}) {
    const {stageSize} = useStage();
    const playerViewPort = usePlayerViewPort();
    const {combatants} = useCombatants();
    const {dungeonSettings} = useDungeon();
    const encounter = useEncounter();
    const displayedViewport = useInterpolatedPlayerViewport(playerViewPort,
        `${encounter.id}:${dungeonSettings.levelId}:${stageSize.width}:${stageSize.height}`);
    const mapTransform = coverViewPort(displayedViewport, stageSize);
    const bounds = worldBoundsForViewport(stageSize, mapTransform);
    const {visibility: displayedVisibility, frame, unavailable} = useFogMask(visibility, mapTransform);

    return <>
        <PlayerMapLayers
            transform={mapTransform} visibility={displayedVisibility} fogFrame={frame}
            fogUnavailable={unavailable}
            overlay={<TokenLayer playerView={true} visibility={displayedVisibility} viewportBounds={bounds}/>}
            decorations={
                <>
                    <DungeonGridLayer screenScale={mapTransform.scale}/>
                    {effectsOnLevel(effects, combatants, dungeonSettings.levelId)
                        .filter(effect => effectIntersectsBounds(effect, combatants, bounds)).map(effect =>
                            <AreaEffectAppearance
                                key={effect.id} effect={effect} playerView
                                combatant={combatants.find(actor => actor.id === effect.combatantId)}/>
                        )}
                </>}>
            <DungeonMapLayer/>
        </PlayerMapLayers>
    </>;
}
